import React, { useEffect, useRef, useState } from "react";
import { Loader2, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * Session 30.9 — Manager PIN prompt.
 *
 * Clean, focused dialog used wherever a protected action needs the
 * Manager/Void PIN (closing an unpaid table). `onVerify(pin)` must
 * return `{ ok, error }`; on success `onSuccess()` runs and the dialog
 * closes, on failure the error shows and the dialog stays open.
 */
export function ManagerPinDialog({
  open,
  onOpenChange,
  title = "Manager PIN required",
  description,
  confirmLabel = "Confirm",
  onVerify,
  onSuccess,
  testIdPrefix = "manager-pin",
}) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPin(""); setError(""); setBusy(false);
      setTimeout(() => { try { inputRef.current?.focus(); } catch (_) { /* noop */ } }, 50);
    }
  }, [open]);

  const submit = async () => {
    if (!pin.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      const r = await onVerify(pin.trim());
      if (r && r.ok) {
        onOpenChange(false);
        await onSuccess?.(pin.trim(), r);
      } else {
        setError((r && r.error) || "Incorrect manager PIN.");
        setPin("");
        setTimeout(() => { try { inputRef.current?.focus(); } catch (_) { /* noop */ } }, 30);
      }
    } catch (e) {
      setError(e?.message || "Could not verify the PIN.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="max-w-sm rounded-2xl" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-amber-600 dark:text-amber-400" /> {title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        <div className="space-y-2">
          <Input
            ref={inputRef}
            type="password"
            inputMode="numeric"
            autoComplete="off"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            placeholder="Manager PIN"
            className={`h-12 rounded-xl text-center text-lg tracking-[0.3em] ${error ? "border-destructive focus-visible:ring-destructive" : ""}`}
            aria-invalid={Boolean(error)}
            data-testid={`${testIdPrefix}-input`}
          />
          {error ? <p className="text-sm font-medium text-destructive" role="alert" data-testid={`${testIdPrefix}-error`}>{error}</p> : null}
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" className="rounded-xl" onClick={() => onOpenChange(false)} disabled={busy} data-testid={`${testIdPrefix}-cancel`}>Cancel</Button>
          <Button
            className="rounded-xl bg-[hsl(var(--accent))] font-semibold text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
            onClick={submit}
            disabled={busy || !pin.trim()}
            data-testid={`${testIdPrefix}-confirm`}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
